/**
 * Peças de interface reaproveitadas. Ficam juntas porque são pequenas e sempre
 * usadas em conjunto — um arquivo por botão só espalharia o mesmo Tailwind.
 */
import type { ReactNode } from "react";

export { SearchSelect, SearchSelectField, type SearchSelectOption, type SearchSelectProps } from "./SearchSelect";

export function Card({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-line bg-panel/60 backdrop-blur ${className}`}>{children}</div>
  );
}

export function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled = false,
  className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "primary" | "ghost" | "danger";
  disabled?: boolean;
  className?: string;
}) {
  const styles = {
    primary: "bg-accent text-app hover:bg-sky-400",
    ghost: "bg-subtle/60 text-ink hover:bg-subtle",
    danger: "bg-rose-500/15 text-rose-300 hover:bg-rose-500/25",
  }[variant];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-150 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

export function Badge({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}>
      {children}
    </span>
  );
}

export function Dot({ className = "" }: { className?: string }) {
  return <span className={`inline-block size-2 shrink-0 rounded-full ${className}`} />;
}

/** Rótulo + valor. É o tijolo do painel de detalhe, usado dezenas de vezes. */
/**
 * `raw` desliga a caixa alta. Rótulo escrito para gente ("Velocidade") ganha
 * versalete; identificador técnico, não — `can16bitR5` vira `CAN16BITR5` e deixa
 * de ser reconhecível justamente para quem precisa cruzá-lo com a documentação.
 */
export function Field({
  label,
  value,
  tone = "",
  raw = false,
}: {
  label: string;
  value: ReactNode;
  tone?: string;
  raw?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className={`text-[11px] text-muted ${raw ? "font-mono" : "uppercase tracking-wide"}`}>{label}</dt>
      <dd className={`truncate text-sm text-ink ${tone}`}>{value}</dd>
    </div>
  );
}

export function Spinner({ className = "" }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Carregando"
      className={`inline-block size-4 animate-spin rounded-full border-2 border-line border-t-accent ${className}`}
    />
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 p-8 text-center">
      <p className="text-sm text-muted">{title}</p>
      {hint ? <p className="text-xs text-faint">{hint}</p> : null}
    </div>
  );
}
